import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import GenericTable from '../components/GenericTable';
import { getDateWiseSaleReport } from '../actions/dateWiseReportingActions';
import { fetchCompanies } from '../actions/companyActions';
import CompanySelect from '../components/CompanySelect';
import { getLocalDateRangeForPlayerWise, formatDateLocal } from '../utils/reportDateRange';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

function formatReportDateRangeLabel(filterLabel, startDate, endDate) {
  if (!startDate || !endDate) {
    return filterLabel === 'Between Dates' ? 'Select start and end dates' : '—';
  }
  const from = formatDateLocal(startDate);
  const to = formatDateLocal(endDate);
  const range = from === to ? from : `${from} – ${to}`;
  return `${filterLabel}: ${range}`;
}

const tabOptions = [
  'Today',
  'Yesterday',
  'Current Week',
  'Last Week',
  'Current Month',
  'Between Dates',
];

const DateWiseSaleReport = () => {
  const dispatch = useDispatch();
  const { filterColumns } = useFieldPrivilege();
  const { data, loading, error } = useSelector((state) => state.dateWiseSaleReport);
  const user = useSelector((state) => state.auth.user);
  const companies = useSelector((state) => state.company?.companies ?? []);

  const isSuperAdmin = user?.role === 'super_admin';

  const [tabIndex, setTabIndex] = useState(0);
  const [customRange, setCustomRange] = useState([null, null]);
  const [companyId, setCompanyId] = useState('');

  const currentFilter = tabOptions[tabIndex];

  const [startDate, endDate] = useMemo(() => {
    if (currentFilter === 'Between Dates') return customRange;
    return getLocalDateRangeForPlayerWise(currentFilter);
  }, [currentFilter, customRange]);

  useEffect(() => {
    if (isSuperAdmin) dispatch(fetchCompanies());
  }, [dispatch, isSuperAdmin]);

  // Non–super-admin: use company from login (no company.view privilege needed).
  useEffect(() => {
    if (isSuperAdmin) return;
    const id = user?.companyId || user?.company_id;
    if (id) setCompanyId(String(id));
  }, [isSuperAdmin, user?.companyId, user?.company_id]);

  useEffect(() => {
    if (isSuperAdmin || !user?.companySlug || !companies.length) return;
    const match = companies.find((c) => c.slug === user.companySlug);
    if (match?._id) setCompanyId(String(match._id));
  }, [isSuperAdmin, user?.companySlug, companies]);

  useEffect(() => {
    if (!startDate || !endDate || !companyId) return;
    dispatch(getDateWiseSaleReport({ startDate, endDate, companyId }));
  }, [dispatch, startDate, endDate, companyId]);

  const dateRangeLabel = useMemo(
    () => formatReportDateRangeLabel(currentFilter, startDate, endDate),
    [currentFilter, startDate, endDate]
  );

  const tableData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((row) => ({ ...row, _reportDateRange: dateRangeLabel }));
  }, [data, dateRangeLabel]);

  const columns = useMemo(
    () => [
      { Header: 'Date', accessor: '_reportDateRange' },
      { Header: 'Sale', accessor: 'Sale' },
      { Header: 'Win', accessor: 'Win' },
      { Header: 'Commission', accessor: 'Comm' },
      { Header: 'Profit', accessor: 'NTP' },
    ],
    []
  );

  const visibleColumns = useMemo(
    () => filterColumns('reportDateWise', columns),
    [columns, filterColumns]
  );

  return (
    <Box>
      <Heading mb={4}>Datewise Sale Report</Heading>

      {isSuperAdmin && (
        <Flex mb={4} gap={4} align="center">
          <CompanySelect value={companyId} onChange={setCompanyId} />
        </Flex>
      )}

      <Tabs index={tabIndex} onChange={setTabIndex} mb={4} variant="enclosed">
        <TabList>
          {tabOptions.map((opt) => (
            <Tab key={opt}>{opt}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabOptions.map((opt) => (
            <TabPanel key={opt} p={4}>
              {opt === 'Between Dates' && (
                <Flex gap={4} mb={4} align="center">
                  <DatePicker
                    selected={customRange[0]}
                    onChange={(date) => setCustomRange([date, customRange[1]])}
                    placeholderText="Start Date"
                    selectsStart
                    startDate={customRange[0]}
                    endDate={customRange[1]}
                  />
                  <DatePicker
                    selected={customRange[1]}
                    onChange={(date) => setCustomRange([customRange[0], date])}
                    placeholderText="End Date"
                    selectsEnd
                    startDate={customRange[0]}
                    endDate={customRange[1]}
                    minDate={customRange[0]}
                  />
                </Flex>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {loading ? (
        <Box>Loading...</Box>
      ) : error ? (
        <Box>Error: {error}</Box>
      ) : !companyId ? (
        <Box>
          {isSuperAdmin
            ? 'Please select a company to view this report.'
            : 'Unable to resolve your company. Please sign in again.'}
        </Box>
      ) : Array.isArray(data) && data.length > 0 ? (
        <GenericTable columns={visibleColumns} data={tableData} showColumnFilters={false} compactOnMobile />
      ) : (
        <Box>No data available for this period.</Box>
      )}
    </Box>
  );
};

export default DateWiseSaleReport;
